import { renderOnePageStoreCheckout } from '@shopline/sl-checkout-module/onePageStoreCheckout';
import createSignupLoginModule from '../SignupLogin/createSignupLoginModule';
import { attempt, isError, merge } from 'lodash-es';
import '@shopline/sl-checkout-module/onePageStoreCheckout.css';
import prepareMainConfig from './utils/prepareMainConfig';
import initOnePageStoreToSdk from './utils/initOnePageStoreToSdk';
import onePageStoreEvent from './events';
import currencyService from '../../service/currencyService';
import reloadOnBackForward from './utils/reloadOnBackForward';
import parseEscapeData from '../../utils/parseEscapeData';
import logger from '../../utils/logger';
import { consumeOnePageStoreMountState } from './utils/onePageStoreMountState';
import {
  AUTH_SUCCESS_TOAST_TYPE,
  reloadWithAuthSuccessToast,
} from '../../utils/authSuccessToastQuery';

function mountOnePageStoreCheckout() {
  reloadOnBackForward();

  const appRoot = document.getElementById('one-page-store-checkout-app-root');

  if (!appRoot) return;

  function getCartData() {
    const initCartData = {
      cart: {},
      extraMainConfig: {},
      sessionData: undefined,
    };

    const rawCartData =
      document.getElementById('sl-cart-init-data')?.textContent;
    const cartData = attempt(parseEscapeData, rawCartData);

    if (isError(cartData)) {
      console.error('Failed to get cart data', cartData);
      return initCartData;
    }

    return cartData;
  }

  function getSettingData(preparedMainConfig = {}) {
    const rawSettingData =
      document.getElementById('sl-setting-data')?.textContent;
    const parsedSettingData = attempt(parseEscapeData, rawSettingData);
    const userSettingFromMainConfig =
      preparedMainConfig?.merchantData?.user_setting;

    if (isError(parsedSettingData)) {
      logger.error('Failed to parse One-page-store sl-setting-data');
    }

    // Not available directly from mainConfig; must be provided via sl-setting-data.
    // To avoid issues, fall back to defaults when errors occur.
    const requiredDefaults = {
      lineLoginChannel: null,
      customerAuthenticationOauthApplications: [],
      defaultMembershipPromoBanner: {
        welcome_credits: -1,
        birthday_gift: false,
        birthday_credits: false,
        earn_points_from_order: false,
        membership_tier_discount: false,
      },
      flash: { alert: null },
      ruleManagerExternalHost: '',
      // Benchat Subscription
      // sessionId must come from sl-setting-data because mainConfig.sessionId is base64-encoded.
      sessionId: null,
      benchatSubscriptionConfig: {
        facebook: { enabled: false },
        line: { enabled: false },
      },
    };

    const dataFromExistingSources = {
      // Used by SignupLogin to switch i18n language
      localeData: preparedMainConfig?.localeData,
      // Core SignupLogin user settings mapped from mainConfig.merchantData.user_setting.data
      userSetting: userSettingFromMainConfig,
      // Used by signup copy such as shop name
      merchantData: preparedMainConfig?.merchantData,
      // Used to initialize slFeatureService
      featuresLimit: preparedMainConfig?.featuresLimit,
      // reCAPTCHA flags and keys
      isRecaptchaEnabled: preparedMainConfig?.isRecaptchaEnabled,
      isRecaptchaEnterprise: preparedMainConfig?.isRecaptchaEnterprise,
      recaptchaSiteKey: preparedMainConfig?.recaptchaSiteKey,
      recaptchaEnterpriseCheckboxSiteKey:
        preparedMainConfig?.recaptchaEnterpriseCheckboxSiteKey,
      // Social login flags derived from user setting status
      isFacebookLoginEnabled:
        userSettingFromMainConfig?.facebook_login?.status === 'active',
      isGoogleSignUpLoginEnabled:
        userSettingFromMainConfig?.google_login?.status === 'active',
      isLineLoginEnabled:
        userSettingFromMainConfig?.line_login?.status === 'active',
    };

    const serverSettingData = isError(parsedSettingData)
      ? {}
      : parsedSettingData;

    return merge(
      {},
      requiredDefaults,
      dataFromExistingSources,
      serverSettingData,
    );
  }

  const getGRecaptcha = async () =>
    new Promise((resolve) => {
      const unsubscribe = window.globalSDKObserver?.grecaptcha?.subscribe?.(
        (sdk) => {
          resolve(sdk);
          unsubscribe();
        },
      );
    });

  const { cart, extraMainConfig, paymentUnifyError, sessionData } =
    getCartData();

  // Use one prepared config source so checkout and SignupLogin follow the
  // same precedence rules (window.mainConfig + extraMainConfig).
  const preparedMainConfig = prepareMainConfig(
    window.mainConfig,
    extraMainConfig,
  );
  const settingData = getSettingData(preparedMainConfig);
  const onePageStoreInitialUIState = consumeOnePageStoreMountState();
  const onReload = () =>
    reloadWithAuthSuccessToast(AUTH_SUCCESS_TOAST_TYPE.LOGIN);

  const { pageEvent } = renderOnePageStoreCheckout(appRoot, {
    currencyService,
    mainConfig: preparedMainConfig,
    cart,
    csrfToken: document
      .querySelector('meta[name=csrf-token]')
      ?.getAttribute('content'),
    outerEvent: onePageStoreEvent,
    getGRecaptcha,
    paymentUnifyError,
    sessionData,
    signupLoginModule: createSignupLoginModule(settingData),
    onePageStoreInitialUIState,
    onReload,
  });

  initOnePageStoreToSdk(pageEvent);
}
mountOnePageStoreCheckout();
